import { useCallback, useEffect, useState } from "react";
import Button from "../Common/Button";
import ConfirmModal from "../Common/ConfirmModal";
import Input from "../Common/Input";
import Label from "../Common/Label";
import LoadingSpinner from "../Common/LoadingSpinner";
import Pagination from "../Common/Pagination";
import AccessTypeDropdown from "./AccessTypeDropdown";
import { fieldClassName } from "./formStyles";
import {
  createFMSCustomerApiKey,
  deleteFMSCustomerApiKey,
  getFMSCustomerApiKeys,
  updateFMSCustomerApiKey,
} from "../../lib/api";

const PAGE_SIZE = 10;

const API_KEY_ACCESS_TYPE_OPTIONS = ["READ_WRITE", "READ_ONLY"];

const apiKeyAccessTypeLabels = {
  READ_WRITE: "Read-Write",
  READ_ONLY: "Read-Only",
};

const emptyApiKeyForm = {
  name: "",
  accessType: "READ_ONLY",
};

const StatusBadge = ({ enabled }) => (
  <span
    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
      enabled
        ? "bg-[#DDF5ED] text-[#0B8F6A] dark:bg-[#12403A] dark:text-[#9BE7CE]"
        : "bg-[#FDE8E8] text-[#B42318] dark:bg-[#4A1F1C] dark:text-[#FFD5D0]"
    }`}
    data-testid="api-key-status-badge"
  >
    {enabled ? "Enabled" : "Disabled"}
  </span>
);

const ApiKeysPanel = ({ customerUnid, onNotify }) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [apiKeyForm, setApiKeyForm] = useState(emptyApiKeyForm);
  const [createdKey, setCreatedKey] = useState(null);
  const [apiKeyToToggle, setApiKeyToToggle] = useState(null);
  const [isToggling, setIsToggling] = useState(false);
  const [apiKeyToDelete, setApiKeyToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const pageCount = Math.ceil(totalCount / PAGE_SIZE);

  const loadApiKeys = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getFMSCustomerApiKeys(customerUnid, {
        pageNumber: currentPage + 1,
        pageSize: PAGE_SIZE,
      });
      const payload = response?.data?.data || {};
      setApiKeys(payload.apiKeys || []);
      setTotalCount(payload.count || 0);
    } catch (error) {
      setApiKeys([]);
      setTotalCount(0);
      onNotify(error.message || "Failed to fetch API keys.", "failed");
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, customerUnid, onNotify]);

  useEffect(() => {
    loadApiKeys();
  }, [loadApiKeys]);

  const closeCreateModal = () => {
    if (isCreating) return;
    setIsCreateOpen(false);
    setApiKeyForm(emptyApiKeyForm);
  };

  const handleCreateApiKey = async () => {
    const payload = {
      name: apiKeyForm.name.trim(),
      accessType: apiKeyForm.accessType,
    };

    setIsCreating(true);
    try {
      const response = await createFMSCustomerApiKey(customerUnid, payload);
      const responseData = response?.data?.data || {};
      setIsCreateOpen(false);
      setApiKeyForm(emptyApiKeyForm);
      setCreatedKey({
        key: responseData.key,
        name: responseData.apiKey?.name || payload.name,
      });

      if (currentPage === 0) {
        loadApiKeys();
      } else {
        setCurrentPage(0);
      }
    } catch (error) {
      onNotify(error.message || "Failed to create API key.", "failed");
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleConfirmed = async () => {
    if (!apiKeyToToggle?.unid) return;

    const nextEnabled = !apiKeyToToggle.enabled;
    setIsToggling(true);
    try {
      await updateFMSCustomerApiKey(customerUnid, apiKeyToToggle.unid, {
        name: apiKeyToToggle.name,
        accessType: apiKeyToToggle.accessType,
        enabled: nextEnabled,
      });
      setApiKeyToToggle(null);
      onNotify(
        `API key ${nextEnabled ? "enabled" : "disabled"} successfully.`,
        "success"
      );
      loadApiKeys();
    } catch (error) {
      onNotify(error.message || "Failed to update API key.", "failed");
    } finally {
      setIsToggling(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!apiKeyToDelete?.unid) return;

    setIsDeleting(true);
    try {
      await deleteFMSCustomerApiKey(customerUnid, apiKeyToDelete.unid);
      setApiKeyToDelete(null);
      onNotify("API key deleted successfully.", "success");

      const nextPageCount = Math.ceil(Math.max(totalCount - 1, 0) / PAGE_SIZE);
      if (currentPage > 0 && currentPage >= nextPageCount) {
        setCurrentPage(Math.max(nextPageCount - 1, 0));
      } else {
        loadApiKeys();
      }
    } catch (error) {
      onNotify(error.message || "Failed to delete API key.", "failed");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCopyKey = async () => {
    if (!createdKey?.key) return;
    try {
      await navigator.clipboard.writeText(createdKey.key);
      onNotify("Copied to clipboard.", "success");
    } catch (error) {
      onNotify("Failed to copy to clipboard.", "failed");
    }
  };

  return (
    <div
      className="rounded-2xl border border-[#E6E8EC] bg-white dark:bg-darkThemeModalBg dark:border-white/10 shadow-appShadow overflow-hidden"
      data-testid="customer-api-keys-panel"
    >
      <div className="border-b border-[#E6E8EC] dark:border-white/10 px-5 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-base font-semibold text-dark-grey dark:text-white">API Keys</h3>
          <p className="text-sm text-dark-grey/70 dark:text-white/60 mt-1">
            {totalCount} total — used to authenticate programmatic access on this customer's behalf.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center w-full sm:w-auto">
          <Button
            className="flex items-center justify-center gap-2 border border-theme text-theme px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme/10 transition w-full sm:w-auto"
            onClick={() => {
              window.open("/api-docs", "_blank", "noopener,noreferrer");
            }}
            data-testid="api-documentation-link"
          >
            API Documentation
          </Button>
          <Button
            className="flex items-center justify-center gap-2 bg-theme text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme/90 transition w-full sm:w-auto"
            onClick={() => setIsCreateOpen(true)}
            data-testid="create-api-key-button"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M12 5V19" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M5 12H19" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            Create API Key
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center min-h-48">
          <LoadingSpinner />
        </div>
      ) : apiKeys.length === 0 ? (
        <div className="px-5 py-12 text-center" data-testid="customer-api-keys-empty">
          <p className="text-base font-semibold text-dark-grey dark:text-white">No API keys yet</p>
          <p className="mt-2 text-sm text-dark-grey/70 dark:text-white/60">
            Create the first API key so this customer can call the API programmatically.
          </p>
        </div>
      ) : (
        <div className="divide-y divide-[#E6E8EC] dark:divide-white/10">
          {apiKeys.map((apiKey) => (
            <div
              key={apiKey.unid}
              className="px-5 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
              data-testid={`api-key-row-${apiKey.unid}`}
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm font-semibold text-dark-grey dark:text-white">
                    {apiKey.name}
                  </span>
                  <StatusBadge enabled={apiKey.enabled} />
                </div>
                <p className="mt-1 text-sm text-dark-grey/70 dark:text-white/60">
                  {apiKeyAccessTypeLabels[apiKey.accessType] || apiKey.accessType}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  className={`px-4 py-2 rounded-md border text-sm font-semibold transition ${
                    apiKey.enabled
                      ? "border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white"
                      : "border-[#0B8F6A] text-[#0B8F6A] hover:bg-[#0B8F6A] hover:text-white"
                  }`}
                  onClick={() => setApiKeyToToggle(apiKey)}
                  data-testid={`toggle-api-key-${apiKey.unid}`}
                >
                  {apiKey.enabled ? "Disable" : "Enable"}
                </Button>
                <Button
                  className="px-4 py-2 rounded-md border border-[#EF4444] text-[#EF4444] text-sm font-semibold hover:bg-[#EF4444] hover:text-white transition"
                  onClick={() => setApiKeyToDelete(apiKey)}
                  data-testid={`delete-api-key-${apiKey.unid}`}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pageCount > 1 && (
        <div className="px-5 pb-5">
          <Pagination
            currentPage={currentPage}
            pageCount={pageCount}
            onPageChange={({ selected }) => setCurrentPage(selected)}
          />
        </div>
      )}

      {isCreateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div
            className="w-full max-w-md rounded-2xl bg-white dark:bg-darkThemeModalBg shadow-appShadow"
            data-testid="create-api-key-modal"
          >
            <div className="flex items-center justify-between border-b border-[#E6E8EC] dark:border-white/10 px-6 py-4">
              <h3 className="text-lg font-semibold text-dark-grey dark:text-white">Create API Key</h3>
              <Button
                className="text-dark-grey/70 dark:text-white/70 hover:text-dark-grey dark:hover:text-white"
                onClick={closeCreateModal}
                aria-label="Close"
                data-testid="create-api-key-close"
              >
                ✕
              </Button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="space-y-2">
                <Label className="text-sm text-dark-grey dark:text-white" htmlFor="api-key-name">
                  Name
                </Label>
                <Input
                  id="api-key-name"
                  value={apiKeyForm.name}
                  onChange={(event) => {
                    const { value } = event.target;
                    setApiKeyForm((current) => ({ ...current, name: value }));
                  }}
                  placeholder="e.g. Reporting Integration"
                  className={fieldClassName}
                  data-testid="api-key-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label
                  className="text-sm text-dark-grey dark:text-white"
                  htmlFor="api-key-access-type"
                >
                  Access Type
                </Label>
                <AccessTypeDropdown
                  id="api-key-access-type"
                  value={apiKeyForm.accessType}
                  onSelect={(accessType) => {
                    setApiKeyForm((current) => ({ ...current, accessType }));
                  }}
                  options={API_KEY_ACCESS_TYPE_OPTIONS}
                  labels={apiKeyAccessTypeLabels}
                  data-testid="api-key-access-type-dropdown"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-[#E6E8EC] dark:border-white/10 px-6 py-4">
              <Button
                className="bg-white text-dark-grey border border-[#E6E8EC] dark:border-theme dark:text-white dark:bg-transparent px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme hover:text-white transition-all duration-300 ease-in-out"
                onClick={closeCreateModal}
                data-testid="create-api-key-cancel"
              >
                Cancel
              </Button>
              <Button
                className="bg-theme text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme/90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleCreateApiKey}
                disabled={isCreating || !apiKeyForm.name.trim()}
                data-testid="create-api-key-submit"
              >
                {isCreating ? "Creating..." : "Create API Key"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {createdKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div
            className="w-full max-w-lg rounded-2xl bg-white dark:bg-darkThemeModalBg shadow-appShadow"
            data-testid="api-key-created-modal"
          >
            <div className="border-b border-[#E6E8EC] dark:border-white/10 px-6 py-4">
              <h3 className="text-lg font-semibold text-dark-grey dark:text-white">API Key Created</h3>
              <p
                className="mt-1 text-sm text-dark-grey dark:text-white"
                data-testid="api-key-created-name"
              >
                Key for <span className="font-semibold">{createdKey.name}</span>
              </p>
            </div>
            <div className="px-6 py-4">
              <p className="text-[#B42318] dark:text-[#FFD5D0] text-sm font-semibold">
                This is the only time that the api key can be viewed, so share it with the customer
                and store it securely now. You cannot recover it later. However, you can create a
                new api key at any time.
              </p>

              <div className="mt-4 flex items-center gap-2 rounded-md border border-[#E6E8EC] dark:border-white/10 bg-white dark:darkThemeGradiantBg px-3 py-2">
                <span
                  className="flex-1 break-all font-mono text-sm text-dark-grey dark:text-white select-all"
                  data-testid="api-key-created-value"
                >
                  {createdKey.key}
                </span>
                <Button
                  onClick={handleCopyKey}
                  data-testid="api-key-created-copy"
                  className="shrink-0 px-3 py-1.5 rounded-md border border-theme text-theme text-xs font-semibold hover:bg-theme hover:text-white transition"
                >
                  Copy
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-end border-t border-[#E6E8EC] dark:border-white/10 px-6 py-4">
              <Button
                onClick={() => setCreatedKey(null)}
                data-testid="api-key-created-done"
                className="bg-theme text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-theme/90 transition"
              >
                Done
              </Button>
            </div>
          </div>
        </div>
      )}

      {apiKeyToToggle && (
        <ConfirmModal
          title={apiKeyToToggle.enabled ? "Disable API Key" : "Enable API Key"}
          subText={
            apiKeyToToggle.enabled
              ? "Requests using this key will be rejected until it is re-enabled."
              : "This key will be able to authenticate requests again."
          }
          confirmMessage={`${apiKeyToToggle.enabled ? "Disable" : "Enable"} ${apiKeyToToggle.name}?`}
          cancelText="Cancel"
          submitText={
            isToggling
              ? "Saving..."
              : apiKeyToToggle.enabled
              ? "Disable Key"
              : "Enable Key"
          }
          disableCancel={isToggling}
          disableSubmit={isToggling}
          onCancel={() => {
            if (isToggling) return;
            setApiKeyToToggle(null);
          }}
          onSubmit={handleToggleConfirmed}
        />
      )}

      {apiKeyToDelete && (
        <ConfirmModal
          title="Delete API Key"
          subText="This action cannot be undone."
          confirmMessage={`${apiKeyToDelete.name} will be permanently deleted.`}
          cancelText="Cancel"
          submitText={isDeleting ? "Deleting..." : "Delete API Key"}
          disableCancel={isDeleting}
          disableSubmit={isDeleting}
          onCancel={() => {
            if (isDeleting) return;
            setApiKeyToDelete(null);
          }}
          onSubmit={handleDeleteConfirmed}
        />
      )}
    </div>
  );
};

export default ApiKeysPanel;
